import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { 
  MessageSquare, Mail, Phone, User, Calendar, Search,
  ChevronDown, ChevronUp, Trash2, RefreshCw, Loader2,
  CheckCircle, Clock, XCircle
} from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminMessages = () => {
  const { toast } = useToast();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [expandedMessage, setExpandedMessage] = useState(null);

  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    try {
      const response = await axios.get(`${API}/admin/messages`);
      setMessages(response.data);
    } catch (error) {
      console.error('Error fetching messages:', error);
      toast({
        title: 'Error',
        description: 'Failed to load messages',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const updateMessageStatus = async (id, status) => {
    try {
      await axios.put(`${API}/admin/messages/${id}`, { status });
      setMessages(messages.map(msg => 
        msg.id === id ? { ...msg, status } : msg
      ));
      toast({
        title: 'Success',
        description: `Message marked as ${status}`
      });
    } catch (error) {
      console.error('Error updating message:', error);
      toast({
        title: 'Error',
        description: 'Failed to update message',
        variant: 'destructive'
      });
    }
  };

  const deleteMessage = async (id) => {
    if (!window.confirm('Are you sure you want to delete this message?')) return;
    
    try {
      await axios.delete(`${API}/admin/messages/${id}`);
      setMessages(messages.filter(msg => msg.id !== id));
      toast({
        title: 'Success',
        description: 'Message deleted'
      });
    } catch (error) {
      console.error('Error deleting message:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete message',
        variant: 'destructive'
      });
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const getStatusBadge = (status) => {
    const styles = {
      new: 'bg-blue-100 text-blue-800',
      read: 'bg-gray-100 text-gray-800',
      replied: 'bg-green-100 text-green-800'
    };
    const icons = {
      new: <Clock className="w-3 h-3" />,
      read: <CheckCircle className="w-3 h-3" />,
      replied: <Mail className="w-3 h-3" />
    };
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${styles[status] || styles.new}`}>
        {icons[status] || icons.new}
        {status?.charAt(0).toUpperCase() + status?.slice(1) || 'New'}
      </span>
    );
  };

  const filteredMessages = messages.filter(msg => {
    const matchesSearch = 
      msg.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      msg.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      msg.subject?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      msg.message?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = filterStatus === 'all' || msg.status === filterStatus;
    
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-gray-900">Contact Messages</h2>
          <p className="text-gray-600 mt-2">View and manage messages from your contact form</p>
        </div>
        <Button variant="outline" onClick={fetchMessages}>
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-blue-600">
              {messages.filter(m => m.status === 'new' || !m.status).length}
            </div>
            <div className="text-sm text-blue-800">New</div>
          </CardContent>
        </Card>
        <Card className="bg-gray-50 border-gray-200">
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-gray-600">
              {messages.filter(m => m.status === 'read').length}
            </div>
            <div className="text-sm text-gray-800">Read</div>
          </CardContent>
        </Card>
        <Card className="bg-green-50 border-green-200">
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-green-600">
              {messages.filter(m => m.status === 'replied').length}
            </div>
            <div className="text-sm text-green-800">Replied</div>
          </CardContent>
        </Card>
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-amber-600">
              {messages.length}
            </div>
            <div className="text-sm text-amber-800">Total</div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <Input
            placeholder="Search messages..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="border rounded-lg px-4 py-2 bg-white"
        >
          <option value="all">All Status</option>
          <option value="new">New</option>
          <option value="read">Read</option>
          <option value="replied">Replied</option>
        </select>
      </div>

      {/* Messages List */}
      {filteredMessages.length === 0 ? (
        <Card className="p-12 text-center">
          <CardContent>
            <MessageSquare className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">No Messages</h3>
            <p className="text-gray-500">
              {searchTerm || filterStatus !== 'all' 
                ? 'No messages match your filters'
                : 'No contact form submissions yet'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredMessages.map((msg) => (
            <Card 
              key={msg.id} 
              className={`overflow-hidden transition-all duration-200 ${
                expandedMessage === msg.id ? 'ring-2 ring-amber-400' : ''
              } ${msg.status === 'new' || !msg.status ? 'border-l-4 border-l-blue-500' : ''}`}
            >
              <div
                className="p-4 cursor-pointer hover:bg-gray-50"
                onClick={() => {
                  setExpandedMessage(expandedMessage === msg.id ? null : msg.id);
                  if (msg.status === 'new' || !msg.status) {
                    updateMessageStatus(msg.id, 'read');
                  }
                }}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <User className="w-5 h-5 text-gray-400" />
                      <span className="font-semibold text-gray-900">{msg.name}</span>
                      {getStatusBadge(msg.status)}
                    </div>
                    <div className="flex items-center gap-4 text-sm text-gray-600 mb-2">
                      <span className="flex items-center gap-1">
                        <Mail className="w-4 h-4" /> {msg.email}
                      </span>
                      {msg.phone && (
                        <span className="flex items-center gap-1">
                          <Phone className="w-4 h-4" /> {msg.phone}
                        </span>
                      )}
                    </div>
                    <div className="font-medium text-gray-900">{msg.subject}</div>
                    {expandedMessage !== msg.id && (
                      <p className="text-gray-600 text-sm mt-1 line-clamp-2">{msg.message}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-500 flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      {formatDate(msg.submitted_at)}
                    </span>
                    {expandedMessage === msg.id ? (
                      <ChevronUp className="w-5 h-5 text-gray-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-400" />
                    )}
                  </div>
                </div>
              </div>

              {/* Expanded Content */}
              {expandedMessage === msg.id && (
                <div className="border-t bg-gray-50 p-4">
                  <div className="bg-white rounded-lg p-4 mb-4">
                    <h4 className="font-semibold text-gray-700 mb-2">Message</h4>
                    <p className="text-gray-800 whitespace-pre-wrap">{msg.message}</p>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant={msg.status === 'read' ? 'default' : 'outline'}
                        onClick={(e) => {
                          e.stopPropagation();
                          updateMessageStatus(msg.id, 'read');
                        }}
                        className={msg.status === 'read' ? 'bg-gray-600' : ''}
                      >
                        <CheckCircle className="w-4 h-4 mr-1" /> Mark as Read
                      </Button>
                      <Button
                        size="sm"
                        variant={msg.status === 'replied' ? 'default' : 'outline'}
                        onClick={(e) => {
                          e.stopPropagation();
                          updateMessageStatus(msg.id, 'replied');
                        }}
                        className={msg.status === 'replied' ? 'bg-green-600' : ''}
                      >
                        <Mail className="w-4 h-4 mr-1" /> Mark as Replied
                      </Button>
                      <a
                        href={`mailto:${msg.email}?subject=Re: ${msg.subject}`}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Button size="sm" className="bg-amber-500 hover:bg-amber-600">
                          <Mail className="w-4 h-4 mr-1" /> Reply via Email
                        </Button>
                      </a>
                    </div>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteMessage(msg.id);
                      }}
                    >
                      <Trash2 className="w-4 h-4 mr-1" /> Delete
                    </Button>
                  </div>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminMessages;
